import { Encoding } from '@mode-vegas/vegas';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ErrorReporter } from '@mode/shared/contract-common';
import { MuzeChartRenderer } from '.';
import { VisualizationDataStats } from './muze-chart-renderer';

export const CHUNK_LOAD_ERROR = 'ChunkLoadError';

export enum ChartDataErrorReason {
  TooManyPoints = 'Too Many Points',
  TooManySeries = 'Too Many Series',
  TooManySlices = 'Too Many Slices',
  TooManyFacets = 'Too Many Facets',
  TooManyElements = 'Too Many Elements',
  TooManyPageRows = 'Too Many Page Rows',
  NoData = 'No Data',
}

export class ChartDataError extends Error {
  constructor(
    public reason: ChartDataErrorReason,
    public count: number,
    public limit: number,
    public dataStats: VisualizationDataStats,
    public channel?: string
  ) {
    super(`Data Limit Exceeded, count: ${count}, limit: ${limit}`);
    Object.setPrototypeOf(this, ChartDataError.prototype);
  }
}

export class NoDataError extends Error {
  readonly reason = ChartDataErrorReason.NoData;
  constructor() {
    super(`Data model contains no data`);
    Object.setPrototypeOf(this, NoDataError.prototype);
  }
}

export function getVegasChartDataErrorMsg(err: Error) {
  if (err instanceof ChartDataError) {
    switch (err.reason) {
      case ChartDataErrorReason.TooManyPoints:
        return {
          renderError: `Chart data contains ${err.count} data points, but the limit is ${err.limit}`,
          renderErrorHelp:
            'Displaying this chart could hang your browser. Try adding filters to reduce the number of data points.',
        };
      case ChartDataErrorReason.TooManySeries: {
        return {
          renderError: `Chart data contains ${err.count} series, but the limit is ${err.limit}`,
          renderErrorHelp: `Displaying this chart could hang your browser. Try adding a filter on the ${err.channel} field.`,
        };
      }
      case ChartDataErrorReason.TooManyFacets:
        return {
          renderError: `Chart data contains ${err.count} facets, but the limit is ${err.limit}`,
          renderErrorHelp:
            'Displaying this chart could hang your browser. Try adding a filter on discrete fields in rows and columns.',
        };
      case ChartDataErrorReason.TooManyElements:
        return {
          renderError: `Chart data contains ${err.count} visual elements, but the limit is ${err.limit}`,
          renderErrorHelp:
            'Displaying this chart could hang your browser. Try adding a filter on discrete fields in rows and columns.',
        };
      case ChartDataErrorReason.TooManyPageRows:
        return {
          renderError: `The current page contains ${err.count} rows, but the limit is ${err.limit}`,
          renderErrorHelp:
            'Displaying this chart could hang your browser. Try adding a filter on discrete fields in rows and columns.',
        };
    }
  } else if (err instanceof NoDataError) {
    return {
      renderError: `No data`,
      renderErrorHelp:
        'This chart contains no data or all null data. Try removing some filters or use different fields.',
    };
  } else {
    const { name, message } = err;

    if (name === CHUNK_LOAD_ERROR) {
      return {
        renderError: `Please refresh your browser`,
        renderErrorHelp:
          'We were unable to load a required file for this visualization. This can often be fixed by reloading your browser and/or clearing your browser cache.',
      };
    }

    return {
      renderError: 'Unknown error',
      renderErrorHelp: message,
    };
  }
}

export function notifyMuzeRenderError(
  ErrorReporterService: ErrorReporter,
  error: any,
  canvas: any,
  muzeVersion: string,
  encoding: Encoding,
  chartType?: string | undefined,
  renderContext?: string | undefined,
  canvasState?: string | undefined
) {
  const muzeConfig = getCanvasConfigSummary(canvas?.config());
  const muzeSchema = JSON.stringify(canvas?.data()?.getData()?.schema);
  const muzeColumns = JSON.stringify(canvas?.columns());
  const muzeRows = JSON.stringify(canvas?.rows());
  const muzeLayers = JSON.stringify(canvas?.layers());
  // Strip out the ids and just keep the channel and the value
  const stats = {
    ...error.dataStats,
    layers: (error.dataStats as MuzeChartRenderer.VisualizationDataStats)?.layers?.map((layer) => {
      const record: Record<string, number | undefined> = {};
      Object.entries(layer).forEach(([key, value]) => {
        record[key] = value?.value;
      });
      return record;
    }),
    discreteCount: undefined,
    omittedNullCount: undefined,
  };
  ErrorReporterService.notify({
    error,
    groupingHash: 'MuzeRenderError',
    debugInfo: {
      chartType: chartType,
      context: renderContext,
      reason: error.reason ?? error.message,
      stats: JSON.stringify(stats),
      encoding: JSON.stringify(EncodingHelpers.summarizeEncoding(encoding)),
      schema: muzeSchema,
      rows: muzeRows,
      columns: muzeColumns,
      layers: muzeLayers,
      config: muzeConfig,
      version: muzeVersion,
      canvasState: canvasState,
    },
  });
}

/*
 * @description
 * The Muze canvas config contains a lot of static information (i.e settings which
 * are the same for every visualization), plus some possible user data for color
 * mappings. This function removes that information to shorten the output and hide
 * the user data.
 */
export function getCanvasConfigSummary(config: any) {
  if (config != null) {
    // Replaces a list of ordered values with the number of values
    const stripOrderingValues = (fields: any) => {
      if (fields != null) {
        Object.keys(fields).forEach((field) => {
          if (Array.isArray(fields[field].ordering?.values)) {
            fields[field].ordering.values = fields[field].ordering.values.length;
          }
        });
      }
    };
    // Clean up the legend removing boiler-plate interactions, domain maps and custom sorting
    const legend = { ...config.legend, interaction: {} };
    if (legend.color?.fields != null) {
      Object.keys(legend.color.fields).forEach((field) => {
        legend.color.fields[field]['domainRangeMap'] = undefined;
        legend.color.fields[field]['range'] = undefined;
      });
    }
    stripOrderingValues(legend.color?.fields);
    stripOrderingValues(legend.size?.fields);

    // Clean up the x and y axes
    const axes = { ...config.axes };
    stripOrderingValues(axes.x?.fields);
    stripOrderingValues(axes.y?.fields);

    // Clean up the rows by removing boilerplate interactions and custom sorting
    const rows = { ...config.rows, interaction: undefined };
    stripOrderingValues(rows.facets?.fields);

    // Clean up the columns by removing boilerplate interactions and custom sorting
    const columns = { ...config.rows, interaction: undefined };
    stripOrderingValues(columns.facets?.fields);

    const summary = {
      axes,
      rows,
      columns,
      legend,
    };
    return JSON.stringify(summary);
  }
  return undefined;
}
